import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import FlowRideIcon from '../../image/icons/FlowRideIcon.svg';
import FlowRideIconArchive from '../../image/icons/FlowRideIconArchive.svg';
import MaPositionIcon from '../../image/icons/Inputs/MyLocationIcon.png';
import { type AutocompleteResponse } from '../../types/Autocomplete';
import { TripService } from '../../services/TripService';
import ArrivalIcon from '../../image/icons/Inputs/ArrivalIcon.svg';
import DarkCloseIcon from '../../image/icons/Inputs/DarkCloseIcon.svg';
import { runOnTimeoutIfNotQueued } from '../../tools/DeferredRunTools';
import { formatDate } from '../../tools/TimeRelated';
import { deduplicateHistory, filterSearchHistory } from '../../tools/SearchInputs';
import './ArrivalInput.css';
import ClickOutsideHandler from '../shared/ClickOutsideHandler';
import { type Route } from '../../types/Forms';
import MuiCalendar from '../Calendar/MuiCalendar';
import { type UnifiedItineraryDTO } from '../../types/Itinerary';
import { useMain } from '../../store/StoredContext';
import { useEphemeral } from '../../store/EphemeralContext';
import { IonSpinner } from '@ionic/react';

interface ContainerProps {
  autoComplete: AutocompleteResponse[];
  lat: number;
  lon: number;
  setAutocompleteValues: Dispatch<SetStateAction<AutocompleteResponse[]>>;
  setShowTripsCard: Dispatch<SetStateAction<boolean>>;
  setArrivalSelectedAddress: Dispatch<SetStateAction<AutocompleteResponse | null>>;
  visible: boolean;
  position: string;
  setPosition: Dispatch<SetStateAction<string>>;
  date: number;
  setDate: Dispatch<SetStateAction<number>>;
  setIsEditedDate: Dispatch<SetStateAction<boolean>>;
  isEditedDate: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  setSelectedTrip: Dispatch<SetStateAction<UnifiedItineraryDTO | null>>;
  setTripListHeight: Dispatch<SetStateAction<number>>;
  setAnimated: Dispatch<SetStateAction<boolean>>;
  departureSelectedAddress: AutocompleteResponse | null;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  setITakeMyCarData: Dispatch<SetStateAction<UnifiedItineraryDTO | null>>;
  isMerge: boolean;
  setIsMerge: Dispatch<SetStateAction<boolean>>;
  departureInputRef: React.RefObject<HTMLInputElement>;
  departureInput: string;
  setDepartureInput: Dispatch<SetStateAction<string>>;
  setDepartureSelectedAddress: Dispatch<SetStateAction<AutocompleteResponse | null>>;
  setAutocompleteDepartureValues: Dispatch<SetStateAction<AutocompleteResponse[]>>;
  setItineraries: Dispatch<SetStateAction<Route[]>>;
  setSelectedItinerary: Dispatch<SetStateAction<string>>;
  setGoItineraries: Dispatch<SetStateAction<Route[]>>;
  setBackupReturnItineraries: Dispatch<SetStateAction<Route[]>>;
}

const tripService = new TripService();

const ArrivalInput: React.FC<ContainerProps> = ({
  autoComplete: autoCompleteArrivalValues,
  lat,
  lon,
  setAutocompleteValues: setAutocompleteArrivalValues,
  setShowTripsCard,
  setArrivalSelectedAddress,
  position,
  date,
  setDate,
  setIsEditedDate,
  isEditedDate,
  setVisible,
  setSelectedTrip,
  setTripListHeight,
  setAnimated,
  input,
  setInput,
  setITakeMyCarData,
  isMerge,
  setIsMerge,
  departureInputRef: DepartureInputRef,
  departureInput,
  setDepartureInput,
  setDepartureSelectedAddress,
  setItineraries,
  setSelectedItinerary,
  setGoItineraries,
  setBackupReturnItineraries
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [currentOrSelectedDate, setCurrentOrSelectedDate] = useState<number>(date);
  const ResultsRef = useRef<HTMLDivElement>(null);
  const inputDiv = useRef<HTMLInputElement>(null);
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const [needToCompleteFromGoogle, setNeedToCompleteFromGoogle] = useState<boolean>(true);

  const {
    state: { loadingItinerary }
  } = useEphemeral();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    state: { searchHistory },
    setSearchHistory
  } = useMain();
  const {
    state: { map }
  } = useEphemeral();

  /**
   * Real-time date
   */
  useEffect(() => {
    if (isEditedDate) {
      setCurrentOrSelectedDate(date);
      // setSelectedTrip(journey);
    } else {
      const timerID = setInterval(() => {
        setCurrentOrSelectedDate(Date.now());
      }, 5 * 60000);

      return () => {
        clearInterval(timerID);
      };
    }
  }, [date, isEditedDate]);

  useEffect(() => {
    if (isEditedDate) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCurrentOrSelectedDate(Date.now());
        setDate(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEditedDate, setDate]);

  useEffect(() => {
    if (isMerge && departureInput === '') {
      setDepartureInput('Ma position');
      setDepartureSelectedAddress({
        address: 'Ma position',
        location: {
          lat: -1,
          lon: -1
        },
        source: 'mylocation'
      });
    }
  }, [isMerge, setDepartureSelectedAddress, departureInput, setDepartureInput]);

  // Handle input change and fetch suggestions
  const getSuggestions = useCallback(
    (e: string) => {
      void (async (e: string) => {
        if (e.length === 0) {
          const LatestThree = filterSearchHistory(e, false, searchHistory);
          setAutocompleteArrivalValues([...LatestThree]);
          setShowAutocomplete(true);
          return;
        }

        if (e.length <= 3) {
          const findInLocal = filterSearchHistory(e, false, searchHistory);
          setAutocompleteArrivalValues([...findInLocal]);
          setShowAutocomplete(true);
          return;
        }

        runOnTimeoutIfNotQueued(
          'autoComplete',
          () => {
            setLoading(true);
            tripService
              .autocomplete({
                query: e,
                lat: map?.getCenter().lat ?? 0,
                lon: map?.getCenter().lng ?? 0
              })
              .then((res) => {
                const findInLocalAndGoogle = filterSearchHistory(e, false, [...searchHistory, ...res.data]);
                setAutocompleteArrivalValues([...findInLocalAndGoogle]);
                setNeedToCompleteFromGoogle(true);
                setLoading(false);
                setShowAutocomplete(true);
              })
              .catch((err) => {
                console.log(err);
                setLoading(false);
                setShowAutocomplete(true);
              });
          },
          500
        );
      })(e);
    },
    [searchHistory, setAutocompleteArrivalValues, map]
  );

  const resetAllFields = useCallback(() => {
    setAutocompleteArrivalValues([]);
    setShowAutocomplete(false);
    setSelectedTrip(null);
    setArrivalSelectedAddress(null);
    setTripListHeight(50);
    setAnimated(true);
    setITakeMyCarData(null);
    setNeedToCompleteFromGoogle(true);
  }, [setAnimated, setArrivalSelectedAddress, setAutocompleteArrivalValues, setITakeMyCarData, setSelectedTrip, setTripListHeight]);

  const handleChanges = useCallback(
    (e: string) => {
      setInput(e);
      if (e.length === 0) {
        setNeedToCompleteFromGoogle(false);
      }
      getSuggestions(e);
    },
    [getSuggestions, setInput]
  );

  const handleAddress = useCallback(
    (item: AutocompleteResponse) => {
      if (item.source !== 'mylocation') setSearchHistory(item, false);
      setAutocompleteArrivalValues([]);
      setShowAutocomplete(false);
      setArrivalSelectedAddress(item);
      setInput(item.address);
      setIsMerge(true);
      setAnimated(false);
      setShowTripsCard(true);
      setItineraries([]);
      setSelectedItinerary('');
      setGoItineraries([]);
      setBackupReturnItineraries([]);
    },
    [
      setAnimated,
      setArrivalSelectedAddress,
      setAutocompleteArrivalValues,
      setBackupReturnItineraries,
      setGoItineraries,
      setInput,
      setIsMerge,
      setItineraries,
      setSearchHistory,
      setSelectedItinerary,
      setShowTripsCard
    ]
  );

  const searchGoogle = useCallback(() => {
    void (async () => {
      setLoading(true);
      if (input.length > 3) {
        const resp = await tripService.autocompleteg({
          query: input,
          lat,
          lon
        });

        setAutocompleteArrivalValues(deduplicateHistory([...autoCompleteArrivalValues, ...resp.data]));
        setNeedToCompleteFromGoogle(false);
        if (ResultsRef.current != null) {
          ResultsRef.current.scrollTop = ResultsRef.current.scrollHeight;
        }
      }
      setLoading(false);
    })();
  }, [autoCompleteArrivalValues, input, lat, lon, setAutocompleteArrivalValues]);

  const handleCalendar = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleChangeDeparture = useCallback(() => {
    if (DepartureInputRef.current != null) {
      setDepartureInput('');
      setTimeout(() => {
        DepartureInputRef.current?.focus();
      }, 0);
      // SourceInputRef.current?.select();
      setDepartureSelectedAddress(null);
      setIsMerge(false);
    }
  }, [DepartureInputRef, setDepartureInput, setIsMerge, setDepartureSelectedAddress]);

  const closeAutoComplete = () => {
    setAutocompleteArrivalValues([]);
  };

  return (
    <>
      <div
        className="absolute w-full md:w-3/5 top-0"
        onClick={() => {
          handleCalendar();
        }}
      >
        <span className="absolute text-sm cursor-pointer text-center max-w-[45%] min-w-36 font-bold top-3 h-9 z-40 text-ionic-light md:right-0 right-3 p-2 ml-auto bg-opacity-70 bg-ionic-primary rounded-t-lg backdrop-blur-sm">
          {formatDate(currentOrSelectedDate)}
        </span>
        <MuiCalendar isOpen={isModalOpen} onClose={closeModal} date={date} setDate={setDate} setIsEditedDate={setIsEditedDate} />
      </div>
      <div
        // onClick={e => {
        //   if (inputDiv.current?.value !== '') {
        //     inputDiv.current?.focus();
        //   } else {
        //     inputDiv.current.blur();
        //   }
        // }
        // }
        id="SearchInput"
        className={`absolute right-3 left-3 ml-auto mr-auto ${isMerge ? 'z-30' : 'z-20'} rounded-2xl transition-top duration-300 ease md:w-3/5`}
        style={{
          top: `${position}px`
        }}
      >
        <div
          className="w-full flex h-9"
          onClick={() => {
            handleChangeDeparture();
          }}
        >
          <span
            className={`text-sm pt-2 overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer max-w-[45%] min-w-36 text-center text-ionic-light font-bold rounded-t-lg bg-opacity-70 backdrop-blur-sm bg-gray-500/65 ${isMerge ? 'expanded' : 'normal'}`}
          >
            {departureInput}
          </span>
        </div>
        <div
          className={`bg-white flex items-center shadow-md h-12 p-2 ${isMerge ? 'rounded-b-2xl' : 'rounded-2xl'} relative`}
          style={{
            borderBottomLeftRadius: showAutocomplete ? 0 : '1rem',
            borderBottomRightRadius: showAutocomplete ? 0 : '1rem'
          }}
        >
          <div className="cursor-pointer">
            <img src={ArrivalIcon} />
          </div>
          <div className="w-full px-3 grid">
            <div className="flex">
              <div className="w-[95%] flex items-center justify-center">
                <input
                  ref={inputDiv}
                  placeholder="Arrivée"
                  type="text"
                  value={input}
                  onChange={(e) => {
                    handleChanges(e.target.value);
                  }}
                  onFocus={(e) => {
                    handleChanges(e.target.value);
                  }}
                  // onBlur={() => {
                  //   setAutocompleteValues([]);
                  // }}
                  className="w-full border-none outline-none bg-transparent text-ionic-brand-blue font-medium m-0"
                />
              </div>
              {loadingItinerary && (
                <span className="h-1">
                  <IonSpinner color="primary" className="w-5 mt-[-2px]"></IonSpinner>
                </span>
              )}
              {(showAutocomplete || input.length > 0) && (
                <>
                  <span
                    className="p-1 h-full flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      setVisible(false);
                      setInput('');
                      inputDiv.current?.blur();
                      resetAllFields();
                    }}
                  >
                    <img src={DarkCloseIcon} className="w-[16px]" />
                  </span>
                  {/* <span
                    className="p-1 h-full flex items-center justify-center cursor-pointer"
                    onClick={() => {
                    }}
                  >
                    <IonIcon icon={swapVerticalOutline}></IonIcon>
                  </span> */}
                </>
              )}
            </div>
          </div>
        </div>
        {/* Auto complete response */}
        {showAutocomplete && !loading && (
          <ClickOutsideHandler
            wrapperRef={ResultsRef}
            onOutsideClick={closeAutoComplete}
            className="mp-0 pr-3 pl-3 backdrop-blur-sm bg-white/45 rounded-b-2xl cursor-pointer max-h-96 overflow-y-auto"
          >
            <div
              className="flex p-3 cursor-pointer items-center justify-start text-ionic-brand-blue border-b border-b-ionic-light-gray"
              onClick={() => {
                handleAddress({
                  location: {
                    lat: -1,
                    lon: -1
                  },
                  address: 'Ma position',
                  source: 'mylocation'
                });
              }}
            >
              <img src={MaPositionIcon} className="max-w-7 mr-4" />
              <p className="p-0 m-0 font-medium first-letter:capitalize">Ma position</p>
            </div>
            {autoCompleteArrivalValues.map((item: AutocompleteResponse, index: number) => {
              return (
                <div
                  key={index}
                  className="flex p-3 cursor-pointer items-center justify-start text-ionic-brand-blue border-b border-b-ionic-light-gray"
                  onClick={() => {
                    handleAddress(item);
                  }}
                >
                  {item.source === 'l' ? <img src={FlowRideIconArchive} className="max-w-sm mr-4" /> : <img src={FlowRideIcon} className="max-w-sm mr-4" />}
                  <p className="p-0 m-0 font-medium first-letter:capitalize">{item.address}</p>
                </div>
              );
            })}
            {needToCompleteFromGoogle && (
              <h3
                className="pt-5 pb-5 cursor-pointer w-full font-medium capitalize text-ionic-brand-blue text-center"
                onClick={() => {
                  searchGoogle();
                }}
              >
                Recherche approfondie...
              </h3>
            )}
          </ClickOutsideHandler>
        )}
      </div>
    </>
  );
};

export default ArrivalInput;
